/**
 * One-click fixes for findings with a single obvious answer, done by the editor without the
 * assistant (well under a second): move a wet circuit under the only 30 mA RCD, replace a type
 * AC RCD by type A, set the main RCD to 300 mA, lower a rating to what the cable allows, take
 * the smallest section that brings the voltage drop within the limit, or wire sockets in 2.5 mm².
 */
import { analyse, RCD_DEVICES } from '@elec/analysis';
import { projectCalcs } from '@elec/calc';
import { applyTransaction, type Element, type Id, type Op, type Project } from '@elec/domain';
import { moveCircuitOps } from '@elec/editor';
import { MAX_RATING_A, type Finding } from '@elec/rules';

export type QuickFixKind = 'move30' | 'typeA' | 'main300' | 'rating' | 'section';

export interface QuickFix {
  to: QuickFixKind;
  /** For the button's label: the RCD's tag, the new rating or section. */
  arg: string;
  ops: Op[];
}

const SECTIONS = [1.5, 2.5, 4, 6, 10, 16, 25];
const RULES: Record<string, QuickFixKind> = {
  'R-RCD-02': 'move30',
  'FR-RCD-00': 'move30',
  'NL-RCD-00': 'move30',
  'R-RCD-03': 'typeA',
  'NL-RCD-03': 'typeA',
  'R-RCD-01': 'main300',
  'R-CIR-02': 'rating',
  'FR-CIR-03': 'rating',
  'R-CALC-03': 'section',
  'R-CIR-08': 'section',
};
const mm2 = (n: number) => String(n).replace('.', ',');

/** The finding's one-click fix, when it has a single obvious one; null otherwise. */
export function quickFix(project: Project, f: Finding): QuickFix | null {
  const kind = RULES[f.ruleId];
  const target = f.targets[0];
  if (!kind || !target) return null;
  const an = analyse(project);
  const e = project.elements[target];
  const c = an.circuits.find((x) => x.device.id === target);
  if (kind === 'typeA' || kind === 'main300') {
    if (!e || !RCD_DEVICES.has(e.symbolId)) return null;
    const props = kind === 'typeA' ? { ...e.props, rcdType: 'A' } : { ...e.props, sensitivityMa: 300 };
    return { to: kind, arg: e.tag, ops: [{ op: 'update', coll: 'elements', id: e.id, patch: { props } }] };
  }
  if (!c) return null;
  if (kind === 'move30') {
    // Only when there is exactly one 30 mA RCD to go under: otherwise the assistant asks which.
    const rcds = Object.values(project.elements).filter(
      (x: Element) =>
        x.sheetId === c.sheet.id &&
        RCD_DEVICES.has(x.symbolId) &&
        x.symbolId !== 'iec.rcbo' &&
        Number(x.props.sensitivityMa) <= 30,
    );
    if (rcds.length !== 1 || c.rcds[0]?.id === rcds[0]!.id) return null;
    const ops = moveCircuitOps(project, c.device.id, rcds[0]!.id, an);
    return ops.length ? { to: kind, arg: rcds[0]!.tag, ops } : null;
  }
  if (kind === 'rating') {
    const max = MAX_RATING_A[String(c.cable.sectionMm2)];
    if (!max || !c.ratingA || c.ratingA <= max) return null;
    return {
      to: kind,
      arg: `${max} A`,
      ops: [
        {
          op: 'update',
          coll: 'elements',
          id: c.device.id,
          patch: { props: { ...c.device.props, ratingA: max } },
        },
      ],
    };
  }
  if (!c.circuit || !c.cable.sectionMm2) return null;
  // Sockets on less than 2.5 mm² (R-CIR-08): 2.5 mm², when the rating suits it (else the assistant).
  if (f.ruleId === 'R-CIR-08') {
    if (c.ratingA && c.ratingA > (MAX_RATING_A['2.5'] ?? 20)) return null;
    const cable = { ...c.circuit.cable, sectionMm2: 2.5 };
    return {
      to: kind,
      arg: `${mm2(2.5)} mm²`,
      ops: [{ op: 'update', coll: 'circuits', id: c.circuit.id, patch: { cable } }],
    };
  }
  // The smallest bigger section that brings ΔU within its limit (and keeps Iz ≥ the rating).
  for (const s of SECTIONS.filter((x) => x > c.cable.sectionMm2)) {
    const ops: Op[] = [
      {
        op: 'update',
        coll: 'circuits',
        id: c.circuit.id,
        patch: { cable: { ...c.circuit.cable, sectionMm2: s } },
      },
    ];
    const q = applyTransaction(project, { label: '', ops }).project;
    const k = projectCalcs(q, analyse(q)).get(c.device.id as Id);
    if (
      k?.dropPct !== undefined &&
      k.dropPct <= k.dropLimitPct &&
      (k.iz === undefined || !c.ratingA || k.iz >= c.ratingA)
    )
      return { to: kind, arg: `${mm2(s)} mm²`, ops };
  }
  return null;
}
